import {Button, MenuItem, Paper} from '@material-ui/core';
import {Async, Loaded} from 'Core/models/Async';
import {
  eqSecurityQuestion,
  InvalidSecurityQuestion,
  matchSecurityQuestion,
  SecurityQuestion,
  ValidSecurityQuestion,
} from 'Core/models/SecurityQuestion';
import {Either, fold, isRight, left, right} from 'fp-ts/lib/Either';
import {identity} from 'fp-ts/lib/function';
import {pipe} from 'fp-ts/lib/pipeable';
import {Lens} from 'monocle-ts';
import React, {useState} from 'react';
import {Errors} from 'Shared/components/ErrorPopover';
import OutlinedSelect from 'Shared/components/OutlinedSelect';
import ValidatableTextField from 'Shared/components/ValidatableTextField';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import {notEq} from 'Shared/utilities/equals';
import {saveSecurityQuestions} from 'Users/ducks/userAccount';
import {SecurityQuestionPayload} from 'Users/models/SecurityQuestionPayload';

export type UserSecurityQuestions = {
  securityQuestionOne: SecurityQuestion;
  securityQuestionTwo: SecurityQuestion;
};
export type UserSecurityQuestionForm = Either<UserSecurityQuestions, UserSecurityQuestions>;

type UserSecurityQuestionsSectionProps = {
  userId: string;
  securityQuestions: SecurityQuestionPayload[];
  securityQuestionForm: UserSecurityQuestionForm;
  onChange: (form: Async<UserSecurityQuestionForm>) => void;
  saveSecurityQuestions: typeof saveSecurityQuestions;
} & WithSelectedAgencyProps;

const SecurityQuestionFormLens = Lens.fromProp<UserSecurityQuestions>();
const UserSecurityQuestionsSection = (props: UserSecurityQuestionsSectionProps) => {
  const {selectedAgency} = props;
  const questions = pipe(props.securityQuestionForm, fold(identity, identity));
  const [hasBlurredSecurityQuestionOne, setHasBlurredSecurityQuestionOne] = useState(false);
  const [hasBlurredSecurityQuestionTwo, setHasBlurredSecurityQuestionTwo] = useState(false);
  const [originalQuestions] = useState(questions);
  const isEditing = isRight(props.securityQuestionForm);
  const hasSecurityQuestionsChanged =
    !eqSecurityQuestion.equals(questions.securityQuestionOne, originalQuestions.securityQuestionOne) ||
    !eqSecurityQuestion.equals(questions.securityQuestionTwo, originalQuestions.securityQuestionTwo);

  return (
    <Paper className="user-profile__user-info-section">
      <div className="user-info-section__header">
        <h3>Security Questions</h3>
      </div>
      <div className="user-info-section__name-fields">
        <OutlinedSelect
          disabled={!isEditing}
          label="Security Question One"
          className="user-info-section__profile-input"
          inputProps={{
            id: 'security-question-one',
            name: 'security-question-one',
          }}
          value={questions.securityQuestionOne.question}
          onChange={event => {
            props.onChange(
              new Loaded(
                right(
                  SecurityQuestionFormLens('securityQuestionOne').set(
                    ValidSecurityQuestion.create(event.target.value, questions.securityQuestionOne.answer)
                  )(questions)
                )
              )
            );
          }}
          error={questions.securityQuestionOne instanceof InvalidSecurityQuestion}
        >
          {props.securityQuestions.filter(notEq('id', questions.securityQuestionTwo.question)).map(question => (
            <MenuItem value={question.id} key="security-question-one">
              {question.question}
            </MenuItem>
          ))}
        </OutlinedSelect>
        <ValidatableTextField
          className="user-info-section__profile-input"
          disabled={!isEditing}
          value={questions.securityQuestionOne.answer}
          label="Security Answer 1"
          errors={Errors.standardErrors({
            value: matchSecurityQuestion(questions.securityQuestionOne, {
              Invalid: invalidQuestion => invalidQuestion.errors,
              Unvalidated: () => [],
              Valid: () => [],
            }),
          })}
          showErrors={errors => hasBlurredSecurityQuestionOne && errors.value.length > 0}
          onChange={answer =>
            props.onChange(
              new Loaded(
                right(
                  SecurityQuestionFormLens('securityQuestionOne').set(
                    ValidSecurityQuestion.create(questions.securityQuestionOne.question, answer)
                  )(questions)
                )
              )
            )
          }
          onBlur={() => setHasBlurredSecurityQuestionOne(true)}
        />
        <OutlinedSelect
          disabled={!isEditing}
          label="Security Question Two"
          className="user-info-section__profile-input"
          inputProps={{
            id: 'security-question-two',
            name: 'security-question-two',
          }}
          value={questions.securityQuestionTwo.question || []}
          onChange={event => {
            props.onChange(
              new Loaded(
                right(
                  SecurityQuestionFormLens('securityQuestionTwo').set(
                    ValidSecurityQuestion.create(event.target.value, questions.securityQuestionTwo.answer)
                  )(questions)
                )
              )
            );
          }}
          error={questions.securityQuestionOne instanceof InvalidSecurityQuestion}
        >
          {props.securityQuestions.filter(notEq('id', questions.securityQuestionOne.question)).map(question => (
            <MenuItem value={question.id} key="security-question-two">
              {question.question}
            </MenuItem>
          ))}
        </OutlinedSelect>
        <ValidatableTextField
          value={questions.securityQuestionTwo.answer}
          className="user-info-section__profile-input"
          disabled={!isEditing}
          label="Security Answer 2"
          errors={Errors.standardErrors({
            value: matchSecurityQuestion(questions.securityQuestionTwo, {
              Invalid: invalidQuestion => invalidQuestion.errors,
              Unvalidated: () => [],
              Valid: () => [],
            }),
          })}
          onChange={answer =>
            props.onChange(
              new Loaded(
                right(
                  SecurityQuestionFormLens('securityQuestionTwo').set(
                    ValidSecurityQuestion.create(questions.securityQuestionTwo.question, answer)
                  )(questions)
                )
              )
            )
          }
          showErrors={errors => hasBlurredSecurityQuestionTwo && errors.value.length > 0}
          onBlur={() => setHasBlurredSecurityQuestionTwo(true)}
        />
      </div>
      <div className="user-info-section__actions-container">
        {isEditing ? (
          <>
            <Button
              variant="contained"
              className="user-info-section__cancel-user-information-button"
              onClick={() => {
                props.onChange(new Loaded(left(originalQuestions)));
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={
                !hasSecurityQuestionsChanged ||
                !(questions.securityQuestionOne instanceof ValidSecurityQuestion) ||
                !(questions.securityQuestionTwo instanceof ValidSecurityQuestion)
              }
              color="primary"
              variant="contained"
              className="user-info-section__save-user-information-button"
              onClick={() => {
                props.saveSecurityQuestions(props.userId, questions, selectedAgency?.encryptedIdentifier);
              }}
            >
              Save
            </Button>
          </>
        ) : (
          <Button
            className="user-info-section__edit-button"
            color="primary"
            variant="contained"
            onClick={() => props.onChange(new Loaded(right(questions)))}
          >
            Edit&nbsp;&nbsp;
            <i className="fa fa-edit" />
          </Button>
        )}
      </div>
    </Paper>
  );
};

export default withSelectedAgency(UserSecurityQuestionsSection);
